import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { InquiriesWorkspace } from '../../features/inquiries/InquiriesWorkspace';
import { CATERING_INQUIRIES } from '../../features/inquiries/kind';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { FillPage } from '../../layout/FillPage';
import { Segmented, Skeleton, Workspace } from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  CateringLinks,
  DEFAULT_LINKS_ORDERING,
  useCateringLinks,
} from './CateringLinks';
import {
  type CateringViewTab,
  cateringRecordPath,
  cateringViewPath,
  resolveCateringRoute,
} from './cateringRoutes';

const SECTION_LABEL: Record<CateringViewTab, string> = {
  inquiries: 'Inquiries',
  links: 'Links',
};

async function fetchStoreInfo(): Promise<{
  forward_catering_requests_to_text_link?: boolean | null;
}> {
  const response = await api.get(apiEndpoints.getStoreInfo());
  if (!response?.ok) throw new Error('Store info could not be loaded.');
  return response.json();
}

/**
 * Catering: inquiries Arthur took by phone (/catering/inquiries[/:id]) and,
 * for stores that text an ordering link instead, the links sent
 * (/catering/links). Links lead when the store forwards catering requests to
 * a text link or already has links.
 */
export default function SpecialCatering() {
  const location = useLocation();
  const navigate = useNavigate();
  const route = resolveCateringRoute(location.pathname);

  const storeInfo = useQuery({
    queryKey: useScopedQueryKey('store-info'),
    queryFn: fetchStoreInfo,
  });
  const linksPreview = useCateringLinks(1, DEFAULT_LINKS_ORDERING);
  const ready = !storeInfo.isPending && !linksPreview.isPending;
  const showLinks =
    ready &&
    (storeInfo.data?.forward_catering_requests_to_text_link === true ||
      (linksPreview.data?.totalCount ?? 0) > 0);
  const preferred: CateringViewTab | null = ready
    ? showLinks
      ? 'links'
      : 'inquiries'
    : null;
  const view = route.view ?? preferred;

  useEffect(() => {
    if (route.redirectTo) {
      navigate(
        { pathname: route.redirectTo, search: location.search },
        { replace: true },
      );
      return;
    }
    if (route.view || !preferred) return;
    navigate(
      { pathname: cateringViewPath(preferred), search: location.search },
      { replace: true },
    );
  }, [route.redirectTo, route.view, preferred, location.search, navigate]);

  const sections: CateringViewTab[] =
    preferred === 'links' ? ['links', 'inquiries'] : ['inquiries', 'links'];
  const sectionSwitch =
    showLinks || view === 'links' ? (
      <Segmented<CateringViewTab>
        aria-label="Catering section"
        className="shrink-0"
        items={sections.map((key) => ({ key, label: SECTION_LABEL[key] }))}
        value={view ?? 'inquiries'}
        onChange={(next) => {
          if (next !== view) navigate(cateringViewPath(next));
        }}
      />
    ) : undefined;

  return (
    <FillPage>
      <PageMeta title="AskArthur | Catering" description="Catering inquiries" />
      {view === 'links' ? (
        <CateringLinks sectionSwitch={sectionSwitch} />
      ) : view === 'inquiries' ? (
        <InquiriesWorkspace
          config={CATERING_INQUIRIES}
          selectedId={route.inquiryId}
          listPath={cateringViewPath('inquiries')}
          recordPath={cateringRecordPath}
          sectionSwitch={sectionSwitch}
        />
      ) : (
        <>
          <Workspace title="Catering">
            <div role="status" aria-label="Loading catering" className="p-6">
              <Skeleton className="h-3" width="40%" />
            </div>
          </Workspace>
        </>
      )}
    </FillPage>
  );
}
